import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mountain,
  Tent,
  Waves,
  Camera,
  Users,
  TreePine,
  Compass,
  Sun,
  Heart,
  Footprints,
  Car,
  Bird,
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  Gift,
  Download,
  PartyPopper
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Quiz = () => {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    experience_level: '',
    comfort_zones: [],
    interests: [],
    location_preference: '',
    group_preference: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isBeginnerUser, setIsBeginnerUser] = useState(false);

  const questions = [
    {
      id: 'experience_level',
      title: 'What\'s your outdoor experience level?',
      subtitle: 'Be honest - we want to match you with the right adventures!',
      type: 'single',
      options: [
        { value: 'beginner', label: 'Beginner', description: 'New to outdoor activities', icon: Sparkles },
        { value: 'intermediate', label: 'Intermediate', description: 'Some experience, ready for more', icon: Footprints },
        { value: 'experienced', label: 'Experienced', description: 'Comfortable in most outdoor situations', icon: Mountain }
      ]
    },
    {
      id: 'comfort_zones',
      title: 'What activities interest you?',
      subtitle: 'Select all that you\'d like to explore',
      type: 'multi',
      options: [
        { value: 'hiking', label: 'Hiking', icon: Footprints },
        { value: 'camping', label: 'Camping', icon: Tent },
        { value: 'rv_life', label: 'RV Living', icon: Car },
        { value: 'kayaking', label: 'Water Sports', icon: Waves },
        { value: 'wildlife', label: 'Wildlife Watching', icon: Bird },
        { value: 'photography', label: 'Nature Photography', icon: Camera }
      ]
    },
    {
      id: 'interests',
      title: 'What draws you to the outdoors?',
      subtitle: 'Select all that resonate with you',
      type: 'multi',
      options: [
        { value: 'nature', label: 'Connecting with Nature', icon: TreePine },
        { value: 'fitness', label: 'Physical Fitness', icon: Heart },
        { value: 'social', label: 'Meeting New People', icon: Users },
        { value: 'adventure', label: 'Adventure & Thrills', icon: Compass },
        { value: 'relaxation', label: 'Peace & Relaxation', icon: Sun },
        { value: 'learning', label: 'Learning New Skills', icon: Sparkles }
      ]
    },
    {
      id: 'location_preference',
      title: 'How far are you willing to travel?',
      subtitle: 'For outdoor adventures and activities',
      type: 'single',
      options: [
        { value: 'local', label: 'Local', description: 'Within 50 miles', icon: Compass },
        { value: 'regional', label: 'Regional', description: 'Within my state', icon: Mountain },
        { value: 'national', label: 'National', description: 'Anywhere in the country', icon: TreePine },
        { value: 'international', label: 'International', description: 'I love to travel!', icon: Sun }
      ]
    },
    {
      id: 'group_preference',
      title: 'How do you prefer to explore?',
      subtitle: 'We\'ll match you with the right experiences',
      type: 'single',
      options: [
        { value: 'solo', label: 'Solo', description: 'I enjoy my own company', icon: Footprints },
        { value: 'small_group', label: 'Small Group', description: '2-5 people', icon: Users },
        { value: 'large_group', label: 'Large Group', description: '6+ adventurers', icon: Users },
        { value: 'family', label: 'Family', description: 'With kids or relatives', icon: Heart }
      ]
    }
  ];

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  const handleSelect = (value) => {
    if (currentQuestion.type === 'single') {
      setAnswers({ ...answers, [currentQuestion.id]: value });
    } else {
      const current = answers[currentQuestion.id] || [];
      if (current.includes(value)) {
        setAnswers({ ...answers, [currentQuestion.id]: current.filter(v => v !== value) });
      } else {
        setAnswers({ ...answers, [currentQuestion.id]: [...current, value] });
      }
    }
  };

  const isSelected = (value) => {
    if (currentQuestion.type === 'single') {
      return answers[currentQuestion.id] === value;
    }
    return (answers[currentQuestion.id] || []).includes(value);
  };

  const canProceed = () => {
    const answer = answers[currentQuestion.id];
    if (currentQuestion.type === 'single') {
      return !!answer;
    }
    return answer && answer.length > 0;
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz/submit`, answers, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Update user state
      updateUser({ ...user, quiz_completed: true, quiz_responses: answers });
      
      // Check if beginner to show welcome kit
      const isBeginner = answers.experience_level === 'beginner';
      setIsBeginnerUser(isBeginner);
      
      if (isBeginner) {
        setShowCompletionModal(true);
      } else {
        toast.success('Quiz completed! Let\'s find your perfect matches.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Quiz submit error:', error);
      toast.error('Failed to save quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetWelcomeKit = () => {
    setShowCompletionModal(false);
    navigate('/welcome-kit');
  };

  const handleSkipWelcomeKit = () => {
    setShowCompletionModal(false);
    toast.success('Quiz completed! Let\'s find your perfect matches.');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="quiz-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Logo size="small" />
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="quiz-skip-btn">
            Skip
          </Button>
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-8 pb-4">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Question {currentStep + 1} of {questions.length}
          </p>
        </div>
      </header>

      {/* Question Content */}
      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-10 animate-fade-in">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">{currentQuestion.title}</h1>
            <p className="text-lg text-muted-foreground">{currentQuestion.subtitle}</p>
          </div>

          {/* Options Grid */}
          <div className={`grid gap-4 mb-10 ${currentQuestion.options.length > 4 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
            {currentQuestion.options.map((option, index) => {
              const Icon = option.icon;
              const selected = isSelected(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`quiz-option ${selected ? 'selected' : ''} animate-slide-up`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`quiz-option-${option.value}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{option.label}</h3>
                  {option.description && (
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              data-testid="quiz-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={!canProceed() || submitting}
              className="rounded-full px-8"
              data-testid="quiz-next-btn"
            >
              {submitting ? (
                'Saving...'
              ) : currentStep === questions.length - 1 ? (
                <>
                  Complete <Check className="ml-2 h-4 w-4" />
                </>
              ) : (
                <>
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>

      {/* Welcome Kit Completion Modal for Beginners */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <PartyPopper className="h-10 w-10 text-white" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">Quiz Complete!</DialogTitle>
            <DialogDescription className="text-center">
              Great news! As a beginner, we've prepared a special Welcome Kit just for you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/20 my-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <Gift className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Your First Timer Welcome Kit</h4>
                <p className="text-sm text-muted-foreground">
                  Includes personalized gear checklist, safety tips, and recommended beginner-friendly trails based on your interests.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button onClick={handleGetWelcomeKit} className="w-full rounded-full gap-2" data-testid="get-welcome-kit-btn">
              <Download className="h-4 w-4" />
              Get My Welcome Kit
            </Button>
            <Button variant="ghost" onClick={handleSkipWelcomeKit} data-testid="skip-welcome-kit-btn">
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quiz;
